<template>
  <div
    v-if="currentVariant !== 'control'"
    class="pdp-discount-incentive"
    :class="[
      `pdp-discount-incentive--case-${incentiveCase}`,
      { 'pdp-discount-incentive--unlocked': incentiveCase >= 3 }
    ]"
  >
    <!-- Header Row Inside Unified Card Block -->
    <div
      class="pdp-discount-incentive__header"
      :class="{ 'pdp-discount-incentive__clickable': bestSellerProducts.length }"
      @click="toggleExpand"
    >
      <div class="pdp-discount-incentive__header-main">
        <!-- ICON AREA: Red Gift for Case 1 & 2 | Green Checkmark for Case 3, 4, 5 -->
        <div class="pdp-discount-incentive__icon-wrapper">
          <!-- Gift Icon for Case 1 & 2 -->
          <svg
            v-if="incentiveCase <= 2"
            class="pdp-discount-incentive__gift-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20 12v10H4V12" />
            <path d="M2 7h20v5H2z" />
            <path d="M12 22V7" />
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
          </svg>

          <!-- Checkmark Icon for Case 3, 4, 5 -->
          <svg
            v-else
            class="pdp-discount-incentive__check-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.707 7.707a1 1 0 00-1.414-1.414L11 12.586 8.707 10.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l5-5z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        <!-- TEXT CONTENT AREA BY CASE -->
        <div class="pdp-discount-incentive__content">
          <!-- CASE 1: Giỏ hàng 0 item -->
          <template v-if="incentiveCase === 1">
            <div class="pdp-discount-incentive__primary-title">
              Buy more, save more
            </div>
            <div class="pdp-discount-incentive__case1-tiers">
              <div class="pdp-discount-incentive__case1-tier">
                <span class="pdp-discount-incentive__case1-count">2 items</span>
                <span class="pdp-discount-incentive__case1-discount">5% OFF</span>
              </div>
              <div class="pdp-discount-incentive__case1-divider"></div>
              <div class="pdp-discount-incentive__case1-tier">
                <span class="pdp-discount-incentive__case1-count">3 items</span>
                <span class="pdp-discount-incentive__case1-discount">10% OFF</span>
              </div>
              <div class="pdp-discount-incentive__case1-divider"></div>
              <div class="pdp-discount-incentive__case1-tier">
                <span class="pdp-discount-incentive__case1-count">4+ items</span>
                <span class="pdp-discount-incentive__case1-discount">15% OFF</span>
              </div>
            </div>
          </template>

          <!-- CASE 2: Giỏ hàng 1 item -->
          <template v-else-if="incentiveCase === 2">
            <div class="pdp-discount-incentive__primary-text">
              Add 1 more item to get <span class="highlight-red">5% OFF</span>
            </div>
            <div class="pdp-discount-incentive__secondary-text">
              2 items: 5% OFF | 3 items: 10% OFF
            </div>
          </template>

          <!-- CASE 3: Giỏ hàng 2 items -->
          <template v-else-if="incentiveCase === 3">
            <div class="pdp-discount-incentive__primary-text">
              You unlocked <span class="highlight-green">5% OFF!</span>
            </div>
            <div class="pdp-discount-incentive__secondary-text">
              Add 1 more item to get 10% OFF
            </div>
          </template>

          <!-- CASE 4: Giỏ hàng 3 items -->
          <template v-else-if="incentiveCase === 4">
            <div class="pdp-discount-incentive__primary-text">
              You unlocked <span class="highlight-green">10% OFF!</span>
            </div>
            <div class="pdp-discount-incentive__secondary-text">
              Add 1 more item to get 15% OFF
            </div>
          </template>

          <!-- CASE 5: Giỏ hàng >= 4 items -->
          <template v-else-if="incentiveCase === 5">
            <div class="pdp-discount-incentive__primary-text">
              You got our best deal!
            </div>
            <div class="pdp-discount-incentive__secondary-text">
              Enjoy 15% OFF 🎉
            </div>
          </template>
        </div>
      </div>

      <!-- Mũi tên mở rộng accordion (Up/Down) -->
      <svg
        v-if="bestSellerProducts.length"
        class="pdp-discount-incentive__arrow-icon"
        :class="{ 'pdp-discount-incentive__arrow-icon--expanded': isExpanded }"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>

    <!-- Recommendations Body Directly Inside the Unified Card Block -->
    <transition name="pdp-incentive-expand">
      <div
        v-show="isExpanded && bestSellerProducts.length"
        class="pdp-discount-incentive__body"
      >
        <div class="pdp-discount-incentive__complete-title">
          Complete your gift
        </div>
        <div class="pdp-discount-incentive__complete-subtitle">
          Customers also love these:
        </div>

        <div class="pdp-discount-incentive__best-sellers">
          <div
            v-for="item in bestSellerProducts"
            :key="item.id"
            class="pdp-discount-incentive__card"
            @click="navigateToProduct(item)"
          >
            <div class="pdp-discount-incentive__card-img-wrapper">
              <img :src="item.image" :alt="item.title" class="pdp-discount-incentive__card-img" />
            </div>
            <div class="pdp-discount-incentive__card-title" :title="item.title">
              {{ item.title }}
            </div>
            <div class="pdp-discount-incentive__card-price">
              <span>{{ formatPrice(item.price) }}</span>
              <span
                v-if="item.compareAtPrice && item.compareAtPrice > item.price"
                class="pdp-discount-incentive__card-price-compare"
              >
                {{ formatPrice(item.compareAtPrice) }}
              </span>
            </div>
            <button
              class="pdp-discount-incentive__card-btn"
              @click.stop="navigateToProduct(item)"
            >
              Customize
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useABTestStoreV2 from '@/composables/abtest/store'
import useProductStore from '@/store/product'
import useCartStore from '@/store/cart'
import useUserStore from '@/store/user'
import useDiscountStore from '@/store/discount'
import { formatPrice, isProductHasTags } from '@/utils/product'
import {
  PRODUCT_TAG_APPLY_FAKE_PRICE_BF9_BUT_NO_DISCOUNT,
  CAMPAIGN_FAKE_PRICE_BF9_BUT_NO_DISCOUNT,
} from '@/utils'
import { useApplyFakePriceDiscountBF9 } from '@/pages/product/composables/ProductFakePrice'

import Analytics from '@/services/analytics'

const { applyFakePriceDiscountBF9 } = useApplyFakePriceDiscountBF9()

interface BestSellerItem {
  id: string
  title: string
  price: number
  compareAtPrice?: number
  image: string
  handle: string
  variantId?: string
}

const route = useRoute()
const router = useRouter()
const abtestStore = useABTestStoreV2()
const productStore = useProductStore()
const cartStore = useCartStore()
const userStore = useUserStore()
const discountStore = useDiscountStore()
const isExpanded = ref(false)
const hasTrackedFirstOpen = ref(false)

const EVENT_PDP_DISCOUNT_INCENTIVE = 'PDP Discount Incentive Action'

function trackIncentiveEvent(action: string, payload: Record<string, any> = {}) {
  try {
    Analytics.track(EVENT_PDP_DISCOUNT_INCENTIVE, {
      action,
      product: productStore.product?.handle,
      incentive_case: incentiveCase.value,
      ...payload,
    })
  } catch (error: any) {
    try {
      Analytics.log('pdp_discount_incentive_track_error', {
        action,
        message: error?.message || error,
        product: productStore.product?.handle,
      })
    } catch (sentryErr) {}
  }
}

function toggleExpand() {
  if (bestSellerProducts.value.length === 0) return
  isExpanded.value = !isExpanded.value
  
  if (isExpanded.value) {
    loadBestSellers()
    
    // Tracking chỉ cho lần mở đầu tiên
    if (!hasTrackedFirstOpen.value) {
      hasTrackedFirstOpen.value = true
      trackIncentiveEvent('open_list', {
        item_count: bestSellerProducts.value.length,
      })
    }
  }
}

const currentVariant = computed<string>(() => {
  const queryVariant = route.query.pdp_discount_variant as string
  if (queryVariant) {
    return queryVariant.toLowerCase()
  }

  return abtestStore.variant?.toLowerCase() || 'control'
})

// Xác định 5 case hiển thị dựa theo số lượng sản phẩm trong giỏ hàng (Cart quantity)
const incentiveCase = computed<number>(() => {
  const count = cartStore.totalCartItems || 0
  if (count <= 0) return 1
  if (count === 1) return 2
  if (count === 2) return 3
  if (count === 3) return 4
  return 5
})

async function loadBestSellers() {
  if (productStore.productRecommendation || productStore.productRecommendationShopify) return
  if (!productStore.product) return

  try {
    const recomendationPayload = {
      product: {
        title: productStore.product.title,
        handle: productStore.product.handle,
        tags: productStore.product.tags || [],
        type: productStore.product.productType || '',
      },
      user_segments: userStore.segments
        ? userStore.segments.map((segment: any) => segment.key)
        : [],
    }
    await productStore.getProductRecommendation(recomendationPayload)

    if (!productStore.productRecommendation?.moreItems?.length) {
      await productStore.getProductRecommendationsShopify({
        id: productStore.product.id,
      })
    }
  } catch (e: any) {
    console.error('Failed to load best sellers recommendation', e)
    try {
      Analytics.log('pdp_discount_incentive_load_bestsellers_error', {
        message: e?.message || e,
        product: productStore.product?.handle,
      })
    } catch (sentryErr) {}
  }
}

onMounted(() => {
  loadBestSellers()
  if (currentVariant.value !== 'control') {
    trackIncentiveEvent('init')
  }
})

watch(
  () => productStore.product?.id,
  (newId) => {
    if (newId) {
      loadBestSellers()
    }
  }
)

const bestSellerProducts = computed<BestSellerItem[]>(() => {
  const rec = productStore.productRecommendation
  const shopifyRec = productStore.productRecommendationShopify

  let list: any[] = []
  if (rec) {
    list = rec.moreItems?.length
      ? rec.moreItems
      : rec.nicheItems?.length
      ? rec.nicheItems
      : rec.similarItems?.length
      ? rec.similarItems
      : rec.productMass || []
  }
  if (!list.length && shopifyRec?.length) {
    list = shopifyRec
  }

  if (!list.length) return []

  return list.slice(0, 8).map((prod: any, i: number) => {
    const rawPrice =
      prod.priceMin ||
      prod.price ||
      prod.variants?.[0]?.price?.amount ||
      prod.variants?.[0]?.price ||
      19.95
    const priceNum = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice) || 19.95
    const rawComparePrice =
      prod.compareAtPrice ||
      prod.compareAtPriceMin ||
      prod.variants?.[0]?.compareAtPrice?.amount ||
      prod.variants?.[0]?.compareAtPrice ||
      0
    const comparePriceNum = typeof rawComparePrice === 'number' ? rawComparePrice : parseFloat(rawComparePrice) || 0
    const imageSrc =
      prod.featuredImage?.src ||
      prod.featuredImage?.url ||
      prod.images?.[0]?.src ||
      prod.images?.[0]?.url ||
      ''
    const variantId = prod.variants?.[0]?.id || prod.firstVariantId
    const handle = prod.handle || ''
    const sku = (prod.variants || [])[0]?.sku
    // Tính toán discount tổng hợp đầy đủ theo logic của ProductListItem (bao gồm fakePriceBF9, packDiscount, v.v.)
    const hasFakePriceBF9ButNoDiscountProductTag = isProductHasTags(
      prod,
      [PRODUCT_TAG_APPLY_FAKE_PRICE_BF9_BUT_NO_DISCOUNT]
    )
    const campaignFakePriceBF9ButNoDiscount =
      discountStore.campaignDiscountCode ==
      CAMPAIGN_FAKE_PRICE_BF9_BUT_NO_DISCOUNT

    let fakePriceBF9Applied = null
    if (
      (hasFakePriceBF9ButNoDiscountProductTag ||
        campaignFakePriceBF9ButNoDiscount) &&
      prod
    ) {
      fakePriceBF9Applied = applyFakePriceDiscountBF9(
        prod,
        priceNum,
        comparePriceNum,
        sku
      )
    }

    const appliedDiscount = fakePriceBF9Applied || discountStore.applyDiscount(
      prod,
      priceNum,
      comparePriceNum,
      sku
    )

    const finalPrice = appliedDiscount ? appliedDiscount.price : priceNum
    const finalComparePrice = appliedDiscount
      ? appliedDiscount.compareAtPrice
      : comparePriceNum

    return {
      id: prod.id || `rec-${i}`,
      title: prod.title || 'Popular Item',
      price: finalPrice,
      compareAtPrice: finalComparePrice,
      image: imageSrc,
      handle,
      variantId,
    }
  })
})

function navigateToProduct(item: BestSellerItem) {
  if (!item.handle) return

  // Tracking mỗi lần click vào sản phẩm
  trackIncentiveEvent('click_item', {
    target_product: item.handle,
  })

  const path = `/products/${item.handle}`
  router.push(path)
}
</script>

<style lang="scss">
.pdp-discount-incentive {
  margin-top: 10px;
  margin-bottom: 12px;
  font-family: inherit;
  width: 0;
  min-width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  background-color: #fff5f5;
  border: 1px solid #fee2e2;
  border-radius: 12px;
  padding: 12px 14px;
  transition: all 0.3s ease;

  &--unlocked {
    background-color: var(--background-color-primary, #eef8f3);
    border-color: #dcfce7;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  .highlight-red {
    color: var(--color-secondary, #eb001b);
    font-weight: 700;
  }

  .highlight-green {
    color: var(--color-primary, #38ba72);
    font-weight: 700;
  }

  /* Header row */
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: default;
    width: 100%;
    max-width: 100%;

    &.pdp-discount-incentive__clickable {
      cursor: pointer;
    }
  }

  &__header-main {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  &__icon-wrapper {
    display: flex;
    align-items: center;
    margin-top: 1px;
    flex-shrink: 0;
  }

  &__gift-icon {
    width: 20px;
    height: 20px;
    color: var(--color-secondary, #eb001b);
    flex-shrink: 0;
  }

  &__check-icon {
    width: 20px;
    height: 20px;
    color: var(--color-primary, #38ba72);
    flex-shrink: 0;
  }

  &__arrow-icon {
    width: 16px;
    height: 16px;
    color: var(--text-color-tertiary, #94a3b8);
    flex-shrink: 0;
    margin-left: 8px;
    transition: transform 0.3s;

    &--expanded {
      transform: rotate(180deg);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  /* Case 1 UI */
  &__primary-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-color-primary, #212529);
    line-height: 1.3;
  }

  &__case1-tiers {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    flex-wrap: wrap;
  }

  &__case1-tier {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
  }

  &__case1-count {
    font-size: 11px;
    color: #991b1b;
    font-weight: 700;
  }

  &__case1-discount {
    font-size: 11px;
    color: #991b1b;
    font-weight: 700;
  }

  &__case1-divider {
    width: 1px;
    height: 22px;
    background-color: #fecaca;
  }

  /* Standard Primary & Secondary Text */
  &__primary-text {
    font-size: 14px;
    color: var(--text-color-primary, #212529);
    font-weight: 700;
    line-height: 1.35;
  }

  &__secondary-text {
    font-size: 12px;
    color: var(--text-color-secondary, #5e5e5e);
    font-weight: 400;
    line-height: 1.3;
  }

  /* Unified Body for Best Seller Recommendations */
  &__body {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid rgba(229, 62, 62, 0.12);
    width: 0;
    min-width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  &--unlocked &__body {
    border-top-color: rgba(34, 197, 94, 0.15);
  }

  &__complete-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--text-color-primary, #212529);
    margin-bottom: 2px;
  }

  &__complete-subtitle {
    font-size: 12px;
    color: var(--text-color-secondary, #5e5e5e);
    margin-bottom: 12px;
  }

  &__best-sellers {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    width: 0;
    min-width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding-bottom: 6px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #fca5a5;
      border-radius: 2px;
    }
  }

  &__card {
    width: 105px;
    flex-shrink: 0;
    background: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 8px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    cursor: pointer;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    }
  }

  &__card-img-wrapper {
    width: 100%;
    aspect-ratio: 1;
    margin-bottom: 6px;
    border-radius: 6px;
    overflow: hidden;
    background: #f8fafc;
  }

  &__card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__card-title {
    font-size: 11px;
    font-weight: 600;
    color: #334155;
    line-height: 1.3;
    height: 28px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 4px;
  }

  &__card-price {
    font-size: 12px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  &__card-price-compare {
    font-size: 10px;
    font-weight: 400;
    color: var(--text-color-tertiary, #94a3b8);
    text-decoration: line-through;
  }

  &__card-btn {
    width: 100%;
    background-color: #fef2f2;
    border: 1px solid #fca5a5;
    color: var(--color-secondary, #eb001b);
    font-weight: 600;
    font-size: 11px;
    border-radius: 6px;
    padding: 4px 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background-color: #fee2e2;
      border-color: #f87171;
    }
  }
}

/* Smooth Expand/Collapse transition for recommendation body */
.pdp-incentive-expand-enter-active {
  transition: all 0.3s ease;
  max-height: 300px;
  opacity: 1;
}

.pdp-incentive-expand-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 300px;
  opacity: 1;
}

.pdp-incentive-expand-enter-from,
.pdp-incentive-expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0 !important;
  padding-top: 0 !important;
  border-top-color: transparent !important;
  overflow: hidden;
}
</style>
